'use client';

import { useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import policyData from '../../parsed-policies.json';
import { SitePage, PageHero, Section, Card, IconBadge, Mark } from '@/components/site/page-kit';

const contactData = policyData.contact;

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact HINCH",
  "url": "https://hinch.in/contact",
  "mainEntity": {
    "@type": "Organization",
    "name": "HINCH",
    "contactPoint": [
      {"@type": "ContactPoint", "telephone": "+91-8309516872", "contactType": "Customer Service", "areaServed": "IN", "availableLanguage": ["English", "Hindi"]},
      {"@type": "ContactPoint", "email": "care@hinch.in", "contactType": "Customer Support", "areaServed": "IN"}
    ]
  }
};

const cardLink = 'block rounded-2xl border border-hv-line bg-white p-6 transition hover:border-hv-ink md:p-8';

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(contactSchema)}}/>
      <SitePage>
        <PageHero
          eyebrow={contactData.contactInformation.companyName}
          title={<>Get in <Mark>Touch</Mark></>}
          description="Have questions? We're here to help. Reach out through any of the channels below."
        />

        <Section>
          <div className="grid max-w-[960px] gap-4 md:gap-5">
            <div className="grid gap-4 sm:grid-cols-2 md:gap-5">
              {/* Phone Card */}
              <a href={`tel:${contactData.contactInformation.phone}`} className={cardLink}>
                <IconBadge icon={Phone} tone="yellow" />
                <h2 className="mt-6 text-lg md:text-xl">Phone</h2>
                <p className="mt-2 text-lg font-semibold text-hv-orange">
                  {contactData.contactInformation.phone}
                </p>
                <div className="mt-4 space-y-1.5 text-sm text-hv-soft">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Mon - Sat: 9:00 AM - 8:00 PM</span>
                  </div>
                  <p className="pl-[22px]">Sunday: 10:00 AM - 6:00 PM</p>
                </div>
              </a>

              {/* Email Card */}
              <a href={`mailto:${contactData.contactInformation.email}`} className={cardLink}>
                <IconBadge icon={Mail} tone="yellow" />
                <h2 className="mt-6 text-lg md:text-xl">Email</h2>
                <p className="mt-2 break-all text-lg font-semibold text-hv-orange">
                  {contactData.contactInformation.email}
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm text-hv-soft">
                  <MessageCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>We&apos;ll respond within 24 hours</span>
                </div>
              </a>
            </div>

            {/* Address Card */}
            <Card className="flex flex-col items-start gap-6 sm:flex-row">
              <IconBadge icon={MapPin} />
              <div>
                <h2 className="text-lg md:text-xl">Office Address</h2>
                <p className="mt-2 whitespace-pre-line text-[15px] leading-[1.8] text-hv-soft">
                  {contactData.contactInformation.address}
                </p>
              </div>
            </Card>
          </div>
        </Section>
      </SitePage>
    </>
  );
}
